import { useId, useMemo, useRef, useState } from 'react';
import type { PostSlackExclusionRequest, SlackConversationsResponse, SlackExclusionsResponse } from '../../shared/api-types.js';
import type { SlackSweepMode } from '../../shared/enums.js';
import { api, type ApiResult } from '../api.js';
import { Button, FailureView, RelativeTime, StateView, StatusPill, TextField, failureDetail, useRequest, useToast } from '../ui/index.js';
import {
  slackCandidateStatus,
  slackChannelLabel,
  slackChannelListEmptyText,
  slackExclusionCandidates,
  slackExclusionRows,
  slackExclusionSummary,
  sweepWithdrawalNote,
} from './lib/slack-connection.js';

// "Channels Calis won't read" (owner ruling 20260926-002133, amended, R7: "Can narrow it"; ADR 0010):
// by default a Slack check may read everything the person can see in Slack — every public channel,
// joined or not, their private channels, and Slack Connect ones — and this list is how they narrow it.
// It starts empty. A channel on it is never read, kept or sent anywhere, and every search match from
// it is dropped before anything else happens. Channels are added from the person's own channel list
// (names only, never messages), found with a filter, or from a finding's citation ("Stop reading #x"),
// which can name a public channel they never joined; each is removed with its own button. Each change
// applies at once and can be undone from its toast (03-components §4.2: undo over confirm). It is a
// list of choices, not a data table, so neither the sort nor the export floor applies (04-data §1).
// Direct messages and group chats are not listed: they are switched on or off together, below. The
// server checks every id again, and only a holder of `slack_connect` may change the list.

export interface SlackExclusionListProps {
  /** Changes whenever the channel list may have changed; the lists reload then. */
  listKey: string;
  /** May change the list (holds `slack_connect`). Without it the list is shown, read-only. */
  canManage: boolean;
  syncInProgress: boolean;
  /** A refresh request is in flight. */
  refreshing: boolean;
  onRefresh: () => void;
  /** This installation's daily Slack sweep: where it sends anything, the intro says what leaving a channel out stops. */
  sweep?: SlackSweepMode;
}

interface ListData {
  channels: SlackConversationsResponse;
  exclusions: SlackExclusionsResponse;
}

const EXCLUSIONS_PATH = '/api/connections/slack/exclusions';

export function SlackExclusionList({ listKey, canManage, syncInProgress, refreshing, onRefresh, sweep = 'off' }: SlackExclusionListProps) {
  const toast = useToast();
  const statusId = useId();
  const request = useRequest<ListData>(async (signal) => {
    // Why both at once: a channel left out is marked with what the channel list says of it, and the
    // channels offered to leave out are the list's, less the ones already out.
    const [channels, exclusions] = await Promise.all([
      api.get<SlackConversationsResponse>('/api/connections/slack/conversations', { signal }),
      api.get<SlackExclusionsResponse>(EXCLUSIONS_PATH, { signal }),
    ]);
    if (!channels.ok) return channels;
    if (!exclusions.ok) return exclusions;
    return { ok: true, status: exclusions.status, data: { channels: channels.data, exclusions: exclusions.data } };
  }, [listKey]);
  const data = request.data;
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const headingRef = useRef<HTMLHeadingElement>(null);
  const withdrawal = sweepWithdrawalNote(sweep, 'channel');
  const filterRef = useRef<HTMLInputElement>(null);

  const channels = useMemo(() => data?.channels.items ?? [], [data]);
  const rows = useMemo(() => (data ? slackExclusionRows(data.exclusions.items, channels) : []), [data, channels]);
  const candidates = useMemo(() => slackExclusionCandidates(channels, query), [channels, query]);

  /** Puts the server's whole answer in place: the list, and which of the person's channels are out. */
  const apply = (exclusions: SlackExclusionsResponse): void => {
    const out = new Set(exclusions.items.map((entry) => entry.conversationId));
    request.setData((current) => (current
      ? { channels: { ...current.channels, items: current.channels.items.map((item) => ({ ...item, excluded: out.has(item.id) })) }, exclusions }
      : current));
  };

  const withBusy = async <T,>(id: string, run: () => Promise<ApiResult<T>>): Promise<ApiResult<T>> => {
    setBusy((current) => new Set(current).add(id));
    try {
      return await run();
    } finally {
      setBusy((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    }
  };

  const leaveOut = async (id: string, label: string, options: { undo: boolean; restore?: string | null } = { undo: true }): Promise<void> => {
    // An "Undo" of reading it again hands back the server's token, so the row returns as it was — the
    // label of a channel never joined included, which nothing else could give it again.
    const body: PostSlackExclusionRequest = { conversationId: id, ...(options.restore ? { restore: options.restore } : {}) };
    const result = await withBusy(id, () => api.post<SlackExclusionsResponse>(EXCLUSIONS_PATH, body));
    if (!result.ok) {
      toast.error(<>We couldn&rsquo;t leave {label} out. {failureDetail(result)}</>);
      return;
    }
    apply(result.data);
    // The channel leaves the offered list: the filter keeps focus, so the next can be found at once.
    requestAnimationFrame(() => filterRef.current?.focus({ preventScroll: true }));
    if (!options.undo) {
      toast.success(`Calis won’t read ${label}.`);
      return;
    }
    toast.success(`Calis won’t read ${label}. Nothing from it is read, kept or sent.`, { action: { label: 'Undo', onClick: () => void readAgain(id, label, { undo: false }) } });
  };

  const readAgain = async (id: string, label: string, options: { undo: boolean } = { undo: true }): Promise<void> => {
    const at = rows.findIndex((row) => row.conversationId === id);
    const result = await withBusy(id, () => api.delete<SlackExclusionsResponse>(`${EXCLUSIONS_PATH}/${encodeURIComponent(id)}`));
    if (!result.ok) {
      toast.error(<>We couldn&rsquo;t let Calis read {label} again. {failureDetail(result)}</>);
      return;
    }
    apply(result.data);
    // The row goes: focus moves to the one that takes its place, or to the list's heading when it was
    // the last (01-foundations §7.2), never to the top of the page.
    requestAnimationFrame(() => {
      const buttons = document.querySelectorAll<HTMLButtonElement>('#slack-exclusions-list .slack-exclusion__remove');
      const next = buttons[Math.min(Math.max(at, 0), buttons.length - 1)];
      if (next) next.focus({ preventScroll: true });
      else headingRef.current?.focus({ preventScroll: true });
    });
    if (!options.undo) {
      toast.success(`Calis may read ${label} again.`);
      return;
    }
    const restore = result.data.restore ?? null;
    toast.success(`Calis may read ${label} again.`, { action: { label: 'Undo', onClick: () => void leaveOut(id, label, { undo: false, restore }) } });
  };

  const refreshButton = canManage ? (
    <Button variant="secondary" icon="refresh" onClick={onRefresh} loading={refreshing} disabled={syncInProgress}>
      {syncInProgress ? 'Refreshing channel list' : 'Refresh channel list'}
    </Button>
  ) : null;

  let listBody;
  if (request.status === 'loading' && !data) {
    listBody = <StateView kind="loading" label="Loading the channels Calis won’t read" minHeight="8rem" compact />;
  } else if (request.status === 'error' && request.error && !data) {
    listBody = <FailureView failure={request.error} what="the channels Calis won’t read" onRetry={request.reload} compact minHeight="8rem" />;
  } else if (rows.length === 0) {
    listBody = (
      <StateView
        kind="empty"
        compact
        icon="check"
        title="Calis may read every channel you can see"
        description={canManage ? 'Leave one out below, or use “Stop reading” on a finding’s message.' : undefined}
      />
    );
  } else {
    listBody = (
      <ul id="slack-exclusions-list" role="list" className="item-list" aria-labelledby="slack-exclusions-heading">
        {rows.map((row) => (
          <li key={row.conversationId} className="slack-exclusion">
            <div className="item-list__main">
              <span className="slack-channel__label">
                <span className="slack-channel__name">{row.label}</span>
                {row.isPrivate ? <StatusPill tone="neutral" icon="lock">Private</StatusPill> : null}
                {row.isExternal ? <StatusPill tone="neutral" icon="users">Shared</StatusPill> : null}
                {!row.inDirectory ? <StatusPill tone="neutral" icon="search">Not one of yours</StatusPill> : null}
              </span>
              <span className="item-list__meta">
                <span>Left out <RelativeTime value={row.createdAt} mode="both" /></span>
              </span>
            </div>
            {canManage ? (
              <div className="item-list__aside">
                <Button
                  variant="secondary"
                  size="sm"
                  icon="x"
                  className="slack-exclusion__remove"
                  loading={busy.has(row.conversationId)}
                  onClick={() => void readAgain(row.conversationId, row.label)}
                >
                  Remove<span className="visually-hidden">: {row.label}</span>
                </Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    );
  }

  let addBody = null;
  if (canManage && data) {
    if (channels.length === 0) {
      // Why the empty state here only: what is left out still shows above, but nothing can be found to
      // add until the channel list has loaded.
      addBody = (
        <StateView
          kind="empty"
          compact
          title="Your channel list hasn’t loaded yet"
          description={slackChannelListEmptyText({ syncInProgress })}
          action={syncInProgress ? undefined : refreshButton}
        />
      );
    } else {
      const status = slackCandidateStatus({ query, matches: candidates.matches, shown: candidates.shown.length, total: candidates.total });
      addBody = (
        <div className="stack stack--2">
          <TextField
            ref={filterRef}
            label="Channel name"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="random"
            autoComplete="off"
            spellCheck={false}
            aria-describedby={statusId}
          />
          <p id={statusId} className="text-sm muted" role="status">{status}</p>
          {candidates.shown.length > 0 ? (
            <ul role="list" className="item-list" aria-label="Your channels Calis may read">
              {candidates.shown.map((channel) => {
                const label = slackChannelLabel(channel);
                return (
                  <li key={channel.id}>
                    <div className="item-list__main">
                      <span className="slack-channel__label">
                        <span className="slack-channel__name">{label}</span>
                        {channel.isPrivate ? <StatusPill tone="neutral" icon="lock">Private</StatusPill> : null}
                        {channel.isExternal ? <StatusPill tone="neutral" icon="users">Shared</StatusPill> : null}
                      </span>
                      {channel.isExternal ? <span className="item-list__meta">Shared with another organisation — read unless you leave it out.</span> : null}
                    </div>
                    <div className="item-list__aside">
                      <Button variant="secondary" size="sm" icon="minus" loading={busy.has(channel.id)} onClick={() => void leaveOut(channel.id, label)}>
                        Leave out<span className="visually-hidden">: {label}</span>
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : candidates.matches === 0 && query.trim() !== '' ? (
            <div className="cluster">
              <Button variant="secondary" icon="x" onClick={() => setQuery('')}>Clear the filter</Button>
            </div>
          ) : null}
          <div className="cluster">{refreshButton}</div>
        </div>
      );
    }
  }

  return (
    <section className="sheet stack stack--3" aria-labelledby="slack-exclusions-heading">
      <div className="stack stack--1">
        <h3 id="slack-exclusions-heading" ref={headingRef} tabIndex={-1}>Channels Calis won’t read</h3>
        <p className="text-sm secondary">
          Calis may read every channel you can see in Slack — public ones you haven’t joined, your private channels and those shared with other organisations too. A channel on this list is never read, kept or sent anywhere, and nothing a search finds in it is used.
          {withdrawal ? ` ${withdrawal}` : ''}
        </p>
        {/* Why only with something on the list: the empty state below already says nothing is left out. */}
        {data && rows.length > 0 ? <p className="text-sm"><strong>{slackExclusionSummary(rows.length)}</strong></p> : null}
      </div>
      {listBody}
      {canManage ? (
        addBody ? (
          <section className="stack stack--2" aria-labelledby="slack-exclusions-add-heading">
            <h4 id="slack-exclusions-add-heading">Leave out one of your channels</h4>
            {addBody}
          </section>
        ) : null
      ) : data ? (
        <p className="text-sm muted">Your access no longer lets you change this list.</p>
      ) : null}
      {/* Why only while a refresh runs: the panel's "Channel list" field says when it was last refreshed, but keeps
          saying so during a re-refresh, so this is then the one helper text beside the disabled button (03-components §2). */}
      {data && channels.length > 0 && syncInProgress ? <p className="text-xs muted">Refreshing your channel list from Slack now.</p> : null}
    </section>
  );
}
